import { useMemo, useState } from "react";
import { Ban, Check, ChevronRight, ExternalLink, Search, Target } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

import { ICP_TENANT_SCOPE, icpRefTitle, icpRefUrl, type AcqIcpItem } from "../../api/acqService";
import { icpSourceLabel } from "../../model/acqLabels";

/** 契约 §6 `attributes` **只认这五个键**,面上也只出这五个。 */
const ATTR_ROWS: { key: keyof NonNullable<AcqIcpItem["attributes"]>; label: string }[] = [
  { key: "industries", label: "行业" },
  { key: "sizes", label: "规模" },
  { key: "regions", label: "地区" },
  { key: "roles", label: "对谁说" },
  { key: "triggers", label: "什么时候" },
];

/**
 * 租户级那一组的标题(契约 §6 保留名 `__tenant__`)。
 * ★ 0917 批次四(契约 §15.3):从「整体」改成「不指定产品」。「整体」听起来像"公司整体战略",
 *   而它真正的意思是**这条画像没挂在任何一条产品线上** —— 老板第 4 条「我都有点看不懂」里
 *   有一半是这个词造成的。
 */
const TENANT_GROUP = "不指定产品";

/** 默认展开前几组:再多一屏就装不下,而这一页要能一眼扫完(契约 §15.3)。 */
const DEFAULT_OPEN_GROUPS = 3;

export interface IcpGroup {
  key: string;
  title: string;
  items: AcqIcpItem[];
}

/**
 * 按产品把画像分组(契约 §13.6:「卖给谁」按产品分组,`__tenant__` 归「不指定产品」)。
 *
 * ★★ 产品名取不到时**不并进「不指定产品」**、也不只写一个光秃秃的 code:
 *   有码查不到名 = **产品已下架**,如实说「xxx(已下架)」。把这种窄画像显示成"全公司口径",
 *   等于悄悄把它的适用面放大,而用户点头时以为自己批的是全公司(`icpScopeLabel` 的老账)。
 * ★ 名字有两次机会:卡自带的 `productName`,和产品树现查的 `nameOf` ——
 *   后者能救回"后端那一刻没 join 上名字"的情况。两次都落空才判已下架。
 * ★ 「不指定产品」排在最后:具体产品的画像更常被改,先让人看见自己的产品线。
 */
export function groupIcpByProduct(
  items: AcqIcpItem[],
  nameOf: (code: string) => string
): IcpGroup[] {
  const groups = new Map<string, IcpGroup>();
  for (const it of items) {
    const code = it.productCode?.trim() || ICP_TENANT_SCOPE;
    const resolved = it.productName?.trim() || nameOf(code);
    const title =
      code === ICP_TENANT_SCOPE
        ? TENANT_GROUP
        : resolved && resolved !== code
          ? resolved
          : `${code}（已下架）`;
    const g = groups.get(code) ?? { key: code, title, items: [] };
    g.items.push(it);
    groups.set(code, g);
  }
  return [...groups.values()].sort((a, b) =>
    a.key === ICP_TENANT_SCOPE ? 1 : b.key === ICP_TENANT_SCOPE ? -1 : 0
  );
}

/** 一条事实挂在哪个产品下(`__tenant__` 一律「不指定产品」,契约 §15.3)。 */
export function scopeLabel(
  item: Pick<AcqIcpItem, "productCode" | "productName">,
  nameOf: (code: string | null | undefined) => string
): string {
  const code = item.productCode?.trim() || ICP_TENANT_SCOPE;
  if (code === ICP_TENANT_SCOPE) return TENANT_GROUP;
  const resolved = item.productName?.trim() || nameOf(code);
  return resolved && resolved !== code ? resolved : `${code}（已下架）`;
}

/**
 * 「卖给谁 / 不要谁」——按产品分组的紧凑事实表(契约 §15.3,0917 批次四重做)。
 *
 * ★★ 上一版是**两栏一条一张卡**:每条画像一张带五行属性、两条出处链接的卡片。
 *   老板第 3 条「卖给谁太长了就不好翻了」说的就是它 —— 一个租户十来条事实就是十来张卡,
 *   一屏放不下三条,而用户在这一页要做的事只有一件:**扫一眼哪些是我认过的**。
 *   所以一条事实缩成一枚 chip,属性/出处/把握挪进点开的浮层 —— 要核对的人点得到,
 *   只是来看一眼的人不用替他翻。
 * ★ 组头带两个数(已确认 / 待确认)而不是一个总数:这一页的全部意义就是"哪些还没点头"。
 * ★ 默认只展开前 3 组,其余折叠 —— 契约 §15.3 要求 1440 下不超过一屏半,
 *   而"超出"要靠**折叠**解决,不是让它长成一条滚不到底的长列。
 * ★ 「不要谁」是**固定的最后一组**并用警示色:它是省钱的那一栏,竖着排的时候
 *   总在最下面没人看见;挤在产品组之间又会被当成"某个产品的画像"。
 */
export function IcpFactBoard({
  icpGroups,
  exclusions,
  nameOf,
  busy,
  onConfirm,
  emptyText,
}: {
  icpGroups: IcpGroup[];
  /** 「不要谁」不按产品分:同行、已签约的这类排除项对哪条产品线都成立。 */
  exclusions: AcqIcpItem[];
  /** 产品码 → 名字(浮层里逐条报"这条挂在哪":「不要谁」那一组的组名说不出这件事)。 */
  nameOf: (code: string | null | undefined) => string;
  busy: boolean;
  onConfirm: (ids: number[]) => void;
  emptyText: string;
}) {
  const [query, setQuery] = useState("");
  const [closed, setClosed] = useState<Record<string, boolean>>({});

  const groups: IcpGroup[] = useMemo(() => {
    const all = [...icpGroups];
    if (exclusions.length > 0) {
      all.push({ key: "__exclusions__", title: "不要谁（这类不接）", items: exclusions });
    }
    if (!query.trim()) return all;
    const q = query.trim();
    // 组名命中就整组留下(「商标注册」这种搜法要的是那一组),否则按值过滤。
    return all
      .map((g) =>
        g.title.includes(q) ? g : { ...g, items: g.items.filter((i) => i.value.includes(q)) }
      )
      .filter((g) => g.items.length > 0);
  }, [icpGroups, exclusions, query]);

  const total = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <section className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h2 className="flex items-baseline gap-x-1.5 text-sm font-bold">
          <Target className="h-4 w-4 shrink-0 translate-y-0.5 text-muted-foreground" />
          卖给谁 · 不要谁
          {total > 0 && (
            <span className="text-2xs font-normal text-muted-foreground">{total} 条</span>
          )}
        </h2>
        {/* ★ 搜索框只在有东西可搜时出现:一个搜不到任何东西的搜索框看起来像页面没加载完。 */}
        {total > 0 && (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜画像里的词…"
              aria-label="搜画像"
              className="h-8 w-44 pl-8 text-xs"
            />
          </div>
        )}
      </div>

      {total === 0 ? (
        <p className="rounded-xl border border-dashed bg-card px-3 py-6 text-center text-xs leading-relaxed text-muted-foreground">
          {query ? "这个词在画像里没有。换个词，或者清空搜索。" : emptyText}
        </p>
      ) : (
        <div className="divide-y rounded-xl border bg-card">
          {groups.map((g, i) => {
            const isExclusion = g.key === "__exclusions__";
            // 搜出来的结果一律展开:搜完还要一组组点开,等于没搜。
            // ★ 「不要谁」**不参与那个前 3 组的名额**:它永远排在最后,按名额算必然被折叠 ——
            //   而它恰恰是省钱的那一组,上一版竖着排时就是这么没人看见的。
            const open = query.trim()
              ? true
              : !(closed[g.key] ?? (!isExclusion && i >= DEFAULT_OPEN_GROUPS));
            const confirmed = g.items.filter((x) => x.confirmed).length;
            const pending = g.items.length - confirmed;
            return (
              <div key={g.key} className={cn(isExclusion && "bg-warning/5")}>
                <button
                  type="button"
                  onClick={() => setClosed((s) => ({ ...s, [g.key]: open }))}
                  className="flex w-full items-center gap-1.5 px-3 py-2 text-left hover:bg-muted/50"
                >
                  <ChevronRight
                    className={cn(
                      "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                      open && "rotate-90"
                    )}
                  />
                  {isExclusion && <Ban className="h-3.5 w-3.5 shrink-0 text-warning" />}
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate text-xs font-semibold",
                      isExclusion && "text-warning"
                    )}
                  >
                    {g.title}
                  </span>
                  {/* ★ 两个数分开报:这一页的全部意义就是"哪些还没点头"。 */}
                  <span className="shrink-0 text-2xs text-muted-foreground">
                    已确认 {confirmed}
                  </span>
                  {pending > 0 && (
                    <span className="shrink-0 text-2xs font-semibold text-warning">
                      待确认 {pending}
                    </span>
                  )}
                </button>
                {open && (
                  <div className="flex flex-wrap gap-1.5 px-3 pb-2.5">
                    {g.items.map((item) => (
                      <IcpChip
                        key={item.factId}
                        item={item}
                        busy={busy}
                        onConfirm={onConfirm}
                        scope={scopeLabel(item, nameOf)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * 一条事实 = 一枚 chip,点开才看细节。
 * ★ 未确认的用虚线 + 警示色描边:它和已生效的那条在打分上是两回事,不能长一样。
 * ★ 细节浮层里保留**出处链接** —— 请人点头之前得让他核得了原文(与商机卡事实块同则)。
 */
function IcpChip({
  item,
  busy,
  onConfirm,
  scope,
}: {
  item: AcqIcpItem;
  busy: boolean;
  onConfirm: (ids: number[]) => void;
  /** 这条挂在哪个产品下。 */
  scope: string;
}) {
  const attrs = item.attributes ?? {};
  // 能点开的出处才算出处:点不开的那条不渲染成链接。
  const refs = (item.sourceRefs ?? []).filter((r) => !!icpRefUrl(r));
  /**
   * 「AI 推的」/「你加的」(契约 §16.6)。
   * ★ **缺席就不出现**:一个把用户自己加的词标成「AI 推的」的小标,会让他不信整页。
   */
  const src = icpSourceLabel(item.source);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-2xs hover:bg-muted",
            item.confirmed ? "bg-background" : "border-dashed border-warning/60 bg-warning/5"
          )}
        >
          <span className="max-w-[16rem] truncate">{item.value}</span>
          {src && <span className="shrink-0 text-muted-foreground">· {src}</span>}
          {item.confirmed ? (
            <Check className="h-3 w-3 shrink-0 text-success" />
          ) : (
            <span className="shrink-0 font-semibold text-warning">待确认</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-2 text-xs">
        <p className="text-sm font-semibold leading-relaxed">{item.value}</p>
        <p className="text-2xs text-muted-foreground">
          {scope}
          {src && ` · ${src}`}
        </p>

        <dl className="space-y-1">
          {ATTR_ROWS.map(({ key, label }) => {
            const vals = attrs[key] ?? [];
            if (vals.length === 0) return null;
            return (
              <div key={key} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <dt className="w-14 shrink-0 text-2xs text-muted-foreground">{label}</dt>
                <dd className="flex min-w-0 flex-wrap gap-1">
                  {vals.map((v) => (
                    <span key={v} className="rounded border bg-background px-1.5 py-px text-2xs">
                      {v}
                    </span>
                  ))}
                </dd>
              </div>
            );
          })}
        </dl>

        {refs.length > 0 && (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="w-14 shrink-0 text-2xs text-muted-foreground">推自</span>
            <div className="flex min-w-0 flex-wrap gap-x-3 gap-y-1">
              {refs.map((r, i) => (
                <a
                  key={`${item.factId}-${i}`}
                  href={icpRefUrl(r)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-0.5 truncate text-2xs font-semibold text-primary underline-offset-2 hover:underline"
                >
                  <span className="truncate">{icpRefTitle(r)}</span>
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-2">
          {item.confidence != null ? (
            <span
              className="text-2xs text-muted-foreground"
              title="AI 对这条的把握程度，越低越该你亲自看一眼"
            >
              把握 {Math.round(item.confidence * 100)}%
            </span>
          ) : (
            <span />
          )}
          {item.confirmed ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-2xs font-semibold text-success">
              <Check className="h-3 w-3" />
              已生效
            </span>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => onConfirm([item.factId])}
              className="h-7 px-2 text-2xs"
            >
              <Check className="h-3.5 w-3.5" />
              确认
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
